import { makeAutoObservable, reaction } from "mobx";
import moment from "moment";
import { accountStore } from "../stores/AccountStore";
import { categoryStore } from "../stores/CategoryStore";
import { PaymentRequestResult } from "./PaymentRequest";

export const NewId = 'new-id';

export const UNKNOWN_USER = '00000000-0000-0000-0000-000000000000';

export interface InvoiceImport {
	id: string;
	paymentType: string;
	vendorId: string;
	vendorAccountNumber?: string;
	contractNumber?: string;
	invoiceNumber: string;
	receivedDate: string;
	invoiceDate: string;
	postingPeriodDate: string;
	dueDate: string;
	invoiceTotal?: number;
	status: InvoiceStatus;
	actor: string;
	statusDate: string;
	statusReason?: string;
	notes?: string;
	attachmentUrl?: string;
	attachment?: InvoiceAttachment;
	invoiceDetails: InvoiceDetailImport[];
	validations?: string;
	firstDescription: string;
}

export type InvoiceDetailImport = Omit<InvoiceDetail, 'id' | 'invoiceId'>;

export type UniqueInvoiceKey = {
	vendorId: string;
	invoiceNumber: string;
};

export type ExistingInvoiceResponse = {
	id: string;
	vendorId: string;
	invoiceNumber: string;
};

export interface Invoice {
	id: string;
	departmentId?: number;
	paymentType: string;
	vendorId: string;
	vendorAccountNumber?: string;
	contractNumber?: string;
	invoiceNumber?: string;
	invoiceTotal?: number;
	receivedDate: string;
	invoiceDate: string;
	dueDate: string;
	attachmentUrl?: string;
	notes?: string;
	status: InvoiceStatus;
	actor: string;
	statusDate: string;
	statusReason?: string;
	postingPeriodDate: string;
	invoiceDetails?: InvoiceDetail[];
	history?: InvoiceStatusDetails[];
	paymentRequests?: PaymentRequestResult[];
	attachment?: InvoiceAttachment;
}

export type InvoiceAttachment = {
	id: number;
	invoiceId?: string;
	driveId?: string;
	fileId?: string;
	fileName?: string;
	fileUrl?: string;
};

export enum InvoiceStatus {
	Approved = 'Approved',
	Created = 'Created',
	Denied = 'Denied',
	Edited = 'Edited',
	EmailSent = 'EmailSent',
	Failed = 'Failed',
	Finalized = 'Finalized',
	Imported = 'Imported',
	InNetSuite = 'InNetSuite',
	PaymentRequestStarted = 'PaymentRequestStarted',
	PaymentRequested = 'PaymentRequested',
	PaymentRequestFailed = 'PaymentRequestFailed',
	Reversed = 'Reversed',
}

export const EDITABLE_STATUSES = [
	InvoiceStatus.Created,
	InvoiceStatus.Edited,
	InvoiceStatus.Imported,
	InvoiceStatus.Denied,
	InvoiceStatus.Reversed,
];

export const APPROVABLE_STATUSES = [
	InvoiceStatus.Created,
	InvoiceStatus.Edited,
	InvoiceStatus.Reversed,
];

const RESETTABLE_STATUSES = [
	InvoiceStatus.Denied,
	InvoiceStatus.Approved,
];

const DELETABLE_STATUSES = [
	InvoiceStatus.Created,
	InvoiceStatus.Denied,
	InvoiceStatus.Edited,
	InvoiceStatus.Reversed,
];

export const FAILED_STATUSES = [
	InvoiceStatus.Denied,
	InvoiceStatus.Failed,
	InvoiceStatus.PaymentRequestStarted,
	InvoiceStatus.PaymentRequestFailed
];

export const DISPATCHABLE_STATUSES = [
	InvoiceStatus.Approved,
	InvoiceStatus.Failed,
	InvoiceStatus.PaymentRequestStarted,
	InvoiceStatus.PaymentRequested,
	InvoiceStatus.PaymentRequestFailed
];

export const COMPLETED_STATUSES = [
	InvoiceStatus.EmailSent,
	InvoiceStatus.Finalized,
	InvoiceStatus.PaymentRequested,
]

export const HAS_PAYMENTREQUESTS = [
	InvoiceStatus.PaymentRequestStarted,
	InvoiceStatus.PaymentRequestFailed,
	InvoiceStatus.PaymentRequested,
	InvoiceStatus.EmailSent,
];

export interface InvoiceDetail {
	id: string,
	invoiceId: string;
	serviceFromDate?: string;
	serviceTillDate?: string;
	amount: number;
	detail: string;
	categoryId: string;
	categoryMarkup: number;
	accountId?: string;
	splitId?: string;
	customerId?: string;
	locationId?: string;
	lineItem?: number;
}

export enum PaymentType {
	NetSuite = 'NetSuite',
	BillPay = 'Prologue Bill Pay',
	BusinessUnitInvoice = 'Business Unit Invoice',
	ExternalCustomerInvoice = 'External Customer Invoice',
}

export type SummaryViewMode = 'Unpaid' | 'ByDates';

export const newInvoice = {
	id: NewId,
	vendorId: '',
	invoiceTotal: 0,
	receivedDate: moment().toISOString(),
	status: InvoiceStatus.Created,
	invoiceDetails: [{
		id: '',
		invoiceId: '',
		amount: 0.00,
		detail: '',
		categoryId: '',
		categoryMarkup: 0,
	}],
} as Invoice;

export class InvoiceModel {
	private disposer?: () => void;
	readonly id: string;
	vendorId?: string;
	vendorAccountNumber?: string;
	contractNumber?: string;
	invoiceNumber?: string;
	invoiceTotal?: number;
	receivedDate: string;
	invoiceDate: string;
	dueDate: string;
	status: InvoiceStatus = InvoiceStatus.Created;
	statusDate: string;
	statusReason?: string;
	actor?: string;
	paymentType?: string;
	invoiceDetails?: InvoiceDetailModel[];
	history?: InvoiceStatusDetails[];
	paymentRequests: PaymentRequestResult[] | undefined = undefined;
	attachment?: InvoiceAttachment;
	attachmentUrl?: string;
	notes?: string;
	postingPeriodDate: string;
	isDirty = false;

	constructor(invoice: Invoice) {
		makeAutoObservable(this, { history: false });
		this.id = invoice.id;
		this.vendorId = invoice.vendorId;
		this.vendorAccountNumber = invoice.vendorAccountNumber;
		this.contractNumber = invoice.contractNumber;
		this.invoiceNumber = invoice.invoiceNumber;
		this.invoiceTotal = invoice.invoiceTotal;
		this.receivedDate = invoice.receivedDate;
		this.invoiceDate = invoice.invoiceDate;
		this.dueDate = invoice.dueDate;
		this.status = invoice.status;
		this.statusDate = invoice.statusDate;
		this.statusReason = invoice.statusReason;
		this.actor = invoice.actor;
		this.paymentType = invoice.paymentType;
		this.attachmentUrl = invoice.attachmentUrl;
		this.notes = invoice.notes;
		this.postingPeriodDate = invoice.postingPeriodDate || invoice.invoiceDate;
		this.invoiceDetails = invoice.invoiceDetails?.map(row => new InvoiceDetailModel(row));
		this.history = invoice.history;
		this.paymentRequests = invoice.paymentRequests;
		this.attachment = invoice.attachment;
		this.setupReactions();
	}

	setupReactions() {
		if (this.isDirty || this.disposer) return;
		this.disposer = reaction(
			() => ({
				vendorId: this.vendorId,
				vendorAccountNumber: this.vendorAccountNumber,
				contractNumber: this.contractNumber,
				invoiceNumber: this.invoiceNumber,
				invoiceTotal: this.invoiceTotal,
				receivedDate: this.receivedDate,
				invoiceDate: this.invoiceDate,
				dueDate: this.dueDate,
				paymentType: this.paymentType,
				attachmentUrl: this.attachmentUrl,
				notes: this.notes,
				postingPeriodDate: this.postingPeriodDate,
				invoiceDetailIds: this.invoiceDetails?.map(row => row.id),
			}),
			() => {
				this.isDirty = true;
				this.dispose();
			}
		);
	}

	get isEditable() {
		return EDITABLE_STATUSES.includes(this.status);
	}

	get hasInvoiceData() {
		const isExternal = this.paymentType === PaymentType.ExternalCustomerInvoice;
		const customerIds = this.invoiceDetails?.map(d => d.customerId) ?? [];
		const hasSameCustomer = customerIds.every(id => id === customerIds[0]);
		return !!this.paymentType && !!this.vendorId && (isExternal || !!this.invoiceNumber) && !!this.invoiceDate && !!this.dueDate && (!isExternal || hasSameCustomer);
	}

	get canApprove() {
		return this.id !== NewId && APPROVABLE_STATUSES.includes(this.status);
	}

	get canDelete() {
		return DELETABLE_STATUSES.includes(this.status);
	}

	get canDispatch() {
		return DISPATCHABLE_STATUSES.includes(this.status);
	}

	get hasPaymentRequests() {
		return HAS_PAYMENTREQUESTS.includes(this.status);
	}

	get canReset() {
		return RESETTABLE_STATUSES.includes(this.status);
	}

	get statusClass() {
		if (FAILED_STATUSES.includes(this.status)) return 'status error';
		if (COMPLETED_STATUSES.includes(this.status)) return 'status success';
		return 'status';
	}

	get externalCustomerId() {
		return this.invoiceDetails?.[0]?.customerId;
	}

	update = (updates: Partial<Invoice>) => {
		Object.assign(this, updates);
		// Make the postingPeriodDate default to invoiceDate
		if ('invoiceDate' in updates && !('postingPeriodDate' in updates)) {
			this.postingPeriodDate = updates.invoiceDate || '';
		}
	}
	setPaymentType(paymentType?: string) {
		this.paymentType = paymentType;
	}
	setInvoiceDetails(invoiceDetails: InvoiceDetailModel[]) {
		this.invoiceDetails = invoiceDetails;
	}

	addInvoiceDetail() {
		const details = this.invoiceDetails ?? [];
		const isExternalInvoice = this.paymentType === PaymentType.ExternalCustomerInvoice;

		const newDetail = new InvoiceDetailModel({
			id: '',
			invoiceId: this.id,
			amount: 0,
			detail: '',
			categoryId: isExternalInvoice ? categoryStore.ITServicesExternalId : '',
			accountId: isExternalInvoice ? accountStore.prologueId : undefined,
			customerId: isExternalInvoice ? this.externalCustomerId : undefined,
			categoryMarkup: 0,
			lineItem: details.length + 1,
		});

		this.setInvoiceDetails([...details, newDetail]);
	}

	removeInvoiceDetail(index: number) {
		if (this.invoiceDetails?.length === 1) {
			return false;
		}

		const details = [...this.invoiceDetails ?? []];
		details.splice(index, 1);
		this.setInvoiceDetails(details);
		return true;
	}

	setCustomerForAllDetails(customerId?: string) {
		this.invoiceDetails?.forEach(d => { d.customerId = customerId; });
	}

	dispose() {
		this.disposer?.();
		this.disposer = undefined;
		this.invoiceDetails?.forEach(row => row.dispose());
	}
}

export class InvoiceDetailModel {
	private disposer?: () => void;
	readonly id: string;
	invoiceId: string;
	serviceFromDate?: string;
	serviceTillDate?: string;
	amount: number;
	detail: string;
	categoryId: string;
	categoryMarkup: number;
	accountId?: string;
	splitId?: string;
	customerId?: string;
	locationId?: string;
	lineItem?: number;
	isDirty = false;

	constructor(invoiceDetail: InvoiceDetail) {
		makeAutoObservable(this);
		this.id = invoiceDetail.id;
		this.invoiceId = invoiceDetail.invoiceId;
		this.serviceFromDate = invoiceDetail.serviceFromDate;
		this.serviceTillDate = invoiceDetail.serviceTillDate;
		this.amount = invoiceDetail.amount;
		this.detail = invoiceDetail.detail;
		this.categoryId = invoiceDetail.categoryId;
		this.categoryMarkup = invoiceDetail.categoryMarkup;
		this.accountId = invoiceDetail.accountId;
		this.splitId = invoiceDetail.splitId;
		this.customerId = invoiceDetail.customerId;
		this.locationId = invoiceDetail.locationId;
		this.lineItem = invoiceDetail.lineItem;
		this.setupReactions();
	}

	setupReactions() {
		if (this.isDirty || this.disposer) return;
		this.disposer = reaction(
			() => ({ ...this }),
			() => {
				this.isDirty = true;
				this.dispose();
			},
		);
	}
	setField<K extends keyof InvoiceDetailModel>(field: K, value: InvoiceDetailModel[K]) {
		(this[field] as InvoiceDetailModel[K]) = value;
	}
	setDetail(detail: string) {
		this.detail = detail;
	}
	dispose() {
		this.disposer?.();
		this.disposer = undefined;
	}
}

export interface InvoiceStatusDetails {
	invoiceId: string;
	status: InvoiceStatus;
	statusDate: string;
	statusReason?: string;
	actor: string;
}

export interface InvoicePostSplit {
	id: string;
	vendorName: string;
	accountNumber: string;
	contractNumber: string;
	invoiceNumber: string;
	receivedDate: string;
	invoiceDate: string;
	dueDate: string;
	currentStatus: string;
	statusDate: string;
	serviceFromDate: string;
	serviceTillDate: string;
	invoiceTotal: number;
	detailAmount: number;
	markup: number;
	splitPercent: number;
	splitAmount: number;
	markedupAmount: number;
	detail: string;
	accountName: string;
	customerName: string;
	categoryName: string;
	locationName: string;
}

export const getInvoiceTotal = (
	invoice: { invoiceDetails?: { amount: number }[]; invoiceTotal?: number },
	fallbackTotal = invoice.invoiceTotal,
) => invoice.invoiceDetails?.reduce((total, detail) => total + detail.amount, 0) ?? fallbackTotal;

export const modelToInvoice = (invoice: InvoiceModel): Invoice => ({
	id: invoice.id,
	vendorId: invoice.vendorId as string,
	vendorAccountNumber: invoice.vendorAccountNumber,
	contractNumber: invoice.contractNumber,
	invoiceNumber: invoice.invoiceNumber,
	invoiceTotal: getInvoiceTotal(invoice),
	receivedDate: invoice.receivedDate,
	invoiceDate: invoice.invoiceDate,
	dueDate: invoice.dueDate,
	attachmentUrl: invoice.attachmentUrl,
	attachment: invoice.attachment,
	notes: invoice.notes,
	status: invoice.status,
	actor: invoice.actor as string,
	statusDate: invoice.statusDate,
	statusReason: invoice.statusReason,
	paymentType: invoice.paymentType as string,
	postingPeriodDate: invoice.postingPeriodDate,
	invoiceDetails: invoice.invoiceDetails?.map(detail => ({
		id: detail.id,
		invoiceId: detail.invoiceId,
		serviceFromDate: detail.serviceFromDate,
		serviceTillDate: detail.serviceTillDate,
		amount: detail.amount,
		detail: detail.detail,
		categoryId: detail.categoryId,
		categoryMarkup: detail.categoryMarkup,
		accountId: detail.accountId,
		splitId: detail.splitId,
		customerId: detail.customerId,
		locationId: detail.locationId,
		lineItem: detail.lineItem,
	})),
})
